// Which claim the editor should open, handed between the ledger and the editor
// page.
//
// Why this exists: Atlas has no router, and A's feature registry renders a page
// with {selectedPractice, features, isAdmin, readOnly, onPageChange} — there is
// no slot for "and this claim". The ledger's Edit action has to name a claim,
// and threading a new prop would mean editing App.js and registry.js, which are
// A's files.
//
// So the id travels through a module both sides already import. This is not new
// state management (frontend.md): it is one module-scoped variable plus a
// sessionStorage mirror, with no subscribers and no re-render semantics — the
// page reads it once on mount, exactly like a navigation argument.
//
// sessionStorage is the mirror rather than the store: a hard refresh on
// /p/3/claim-editor would otherwise lose the claim and drop the biller back to
// the picker. It is per-tab and holds one integer id, never PHI. Every access is
// wrapped because it throws in a private window with site data blocked.

const KEY = "atlas.claimEditor.target";

// {claimId, practiceId}. practiceId is null only when a caller could not say (an older
// call site); such a target is trusted, and the editor's 404 fallback still covers it.
let target = null;

function safe(fn, fallback = null) {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

/** Name the claim the editor should open (and the practice it belongs to), then navigate. */
export function openClaimEditor(claimId, onPageChange, practiceId = null) {
  setClaimEditorTarget(claimId, practiceId);
  if (onPageChange) onPageChange("claim-editor");
}

export function setClaimEditorTarget(claimId, practiceId = null) {
  const id = Number(claimId) || null;
  target = id ? { claimId: id, practiceId: Number(practiceId) || null } : null;
  safe(() => {
    if (target) window.sessionStorage.setItem(KEY, JSON.stringify(target));
    else window.sessionStorage.removeItem(KEY);
  });
}

function stored() {
  if (target) return target;
  const raw = safe(() => window.sessionStorage.getItem(KEY));
  if (!raw) return null;
  const parsed = safe(() => JSON.parse(raw));
  if (parsed && typeof parsed === "object") {
    const id = Number(parsed.claimId) || null;
    target = id ? { claimId: id, practiceId: Number(parsed.practiceId) || null } : null;
  } else {
    // A bare id written before the practice was remembered.
    const id = Number(raw) || null;
    target = id ? { claimId: id, practiceId: null } : null;
  }
  return target;
}

/** The claim to open, or null. Does not clear it — a refresh must still work.
 *
 * Given a practice, a claim remembered for a DIFFERENT practice is forgotten and null is
 * returned: switching practice while the editor was not on screen must not reopen the
 * old practice's claim under the new one (staging walk 9a: a 404 rendered as "Not found"). */
export function readClaimEditorTarget(practiceId = undefined) {
  const current = stored();
  if (!current) return null;
  const asked = Number(practiceId) || null;
  if (practiceId !== undefined && current.practiceId && asked !== current.practiceId) {
    clearClaimEditorTarget();
    return null;
  }
  return current.claimId;
}

export function clearClaimEditorTarget() {
  setClaimEditorTarget(null);
}
